import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, WarningCircle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Callback that Emergent Google Auth redirects to after the admin approves
 * clean-slate. Reads the intent_token from sessionStorage + session_id from
 * the URL hash, calls /clean-slate/verify-google, then navigates back to
 * Settings with the intent_token still valid so the final "type CLEAN-SLATE
 * to confirm" step can complete.
 */
const CleanSlateCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      const hash = location.hash || window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : null;
      const intentToken = sessionStorage.getItem('clean_slate_intent_token');

      if (!sessionId || !intentToken) {
        setStatus('error');
        setError('Verification session expired. Please restart clean-slate.');
        return;
      }
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch (e) { /* noop */ }

      try {
        await axios.post(`${API}/admin/clean-slate/verify-google`, {
          intent_token: intentToken,
          session_id: sessionId,
        });
        sessionStorage.setItem('clean_slate_google_verified', '1');
        // Return to settings — the modal will pick up google_verified=1 and
        // ask for the CLEAN-SLATE acknowledgement to run the final wipe.
        navigate('/admin-settings?section=danger&clean_slate=verified', { replace: true });
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.detail || 'Google verification failed.');
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100 p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'processing' ? (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
              <ShieldCheck size={40} weight="fill" className="text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-testid="clean-slate-processing">
              Verifying with Google for Clean-Slate…
            </h2>
            <p className="text-sm text-gray-500">Please wait while we confirm your identity.</p>
            <div className="mt-6 flex justify-center">
              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <WarningCircle size={40} weight="fill" className="text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-testid="clean-slate-error">
              Verification Failed
            </h2>
            <p className="text-sm text-red-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/admin-settings?section=danger', { replace: true })}
              className="w-full py-3 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition"
              data-testid="clean-slate-back-btn"
            >Back to Danger Zone</button>
          </>
        )}
      </div>
    </div>
  );
};

export default CleanSlateCallback;
